import Seo from "@/components/Seo";
import BusinessApplicationForm from "@/components/BusinessApplicationForm";
import { PHONE_HREF, PHONE_DISPLAY, trackPhoneCall } from "@/lib/contact";
import {
  ShieldCheck,
  Sparkles,
  Cpu,
  MapPin,
  Users,
  Rocket,
  GraduationCap,
  Megaphone,
  Handshake,
  BadgeCheck,
  Phone,
  ArrowRight,
  TrendingUp,
  CalendarClock,
  Wallet,
  CheckCircle2,
} from "lucide-react";

/**
 * StartVendingBusiness — landing page for the $35,000 turnkey
 * "Start Your Own Vending Machine Business" proposal.
 *
 * The content follows the seven proposal sections (Executive Summary,
 * Why High Performance Sites Matter, The Guarantee, What's Included,
 * Timeline, Investment, Benefits). SEO metadata is set via Seo.
 */
export default function StartVendingBusiness() {
  return (
    <div data-testid="start-vending-business-page" className="text-white">
      <Seo />
      <ExecutiveSummary />
      <WhyHighPerformance />
      <TheGuarantee />
      <WhatsIncluded />
      <TimelineSection />
      <InvestmentSection />
      <BenefitsSection />
      <ApplicationSection />
    </div>
  );
}

/* -------------------------- I. Executive Summary -------------------------- */
function ExecutiveSummary() {
  return (
    <section
      data-testid="section-executive-summary"
      className="relative overflow-hidden border-b border-white/5"
    >
      <div className="absolute inset-0 bg-radial-fade" />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
            <Sparkles className="h-3.5 w-3.5" /> Business Opportunity
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            Start Your Own <span className="text-gradient">Vending Machine Business</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            A one-time investment of
            <span className="text-white font-semibold"> $35,000</span> and we
            build the complete system your vending business needs — website,
            inbound + outbound lead generation, marketing toolkit and training —
            then work hands-on with you until you have secured your{" "}
            <span className="text-white font-semibold">first 5 operating sites</span>.
            After that you take everything and run independently.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={Wallet} label="One-time investment" value="$35,000" />
            <StatCard icon={CalendarClock} label="System delivered in" value="2–3 weeks" />
            <StatCard icon={TrendingUp} label="We stay hands-on until" value="5 sites secured" />
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="#apply"
              data-testid="hero-apply-button"
              className="btn-shine neon-glow neon-glow-hover inline-flex items-center gap-2 rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-all"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={PHONE_HREF}
              onClick={trackPhoneCall}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
            >
              <Phone className="h-4 w-4 text-[#FE7200]" /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 shadow-[0_30px_60px_-20px_rgba(254,114,0,0.25)]">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">
              <ShieldCheck className="h-4 w-4" /> Included in $35,000
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              {[
                "Professional website built for your vending business",
                "Inbound lead-gen setup (SEO foundation, content, forms)",
                "Outbound outreach playbook (email, phone, LinkedIn scripts)",
                "Marketing toolkit — proposal decks, flyers, brochures",
                "Full training on operations, pitching and closing",
                "Hands-on coaching until you've secured your first 5 sites",
                "Handover: full system + 5-site profile + machine-supplier intro",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2 text-zinc-200">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FE7200]" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#121212] p-5">
      <Icon className="h-5 w-5 text-[#FE7200]" />
      <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
        {label}
      </div>
      <div className="mt-1 font-display text-xl font-bold text-white">{value}</div>
    </div>
  );
}

/* ------------------- II. Why High Performance Sites Matter ------------------- */
function WhyHighPerformance() {
  const items = [
    {
      icon: Cpu,
      title: "AI-Assisted Site Research",
      text: "We use foot-traffic, demographic and revenue-indicator data to shortlist sites worth pursuing — so your outreach is focused where the odds are best.",
    },
    {
      icon: MapPin,
      title: "Product-to-Site Matching",
      text: "Our playbook helps you match machine type and product mix to the demand profile of each location you approach.",
    },
    {
      icon: Handshake,
      title: "Warm Property-Owner Network",
      text: "Where we already have relationships with property owners, we introduce you directly to accelerate site conversations.",
    },
    {
      icon: Rocket,
      title: "Repeatable Outreach System",
      text: "Email templates, cold-call scripts and LinkedIn sequences you can run again and again as you grow past the first 5 sites.",
    },
  ];
  return (
    <section
      data-testid="section-why-high-performance"
      className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5"
    >
      <SectionEyebrow>Section II</SectionEyebrow>
      <SectionTitle>How we help you find the right sites</SectionTitle>
      <p className="mt-4 max-w-3xl text-zinc-300">
        Vending performance ultimately depends on the site. Instead of promising
        revenue we can't control, we give you a repeatable system — research
        tools, matching heuristics, warm introductions and outreach scripts — so
        you're spending your time on locations that are worth chasing.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {items.map((it) => (
          <div
            key={it.title}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6"
          >
            <it.icon className="h-6 w-6 text-[#FE7200]" />
            <div className="mt-3 font-display text-lg font-bold">{it.title}</div>
            <p className="mt-1.5 text-sm text-zinc-300 leading-relaxed">{it.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------- III. How We Work With You -------------------------- */
function TheGuarantee() {
  const steps = [
    {
      n: "01",
      title: "We build the system",
      body: "Website, inbound funnel, outbound outreach playbook and marketing toolkit — all built and handed to you within the first 2–3 weeks.",
    },
    {
      n: "02",
      title: "We stay hands-on until 5 sites",
      body: "Weekly coaching, warm introductions where available, and joint execution on outreach — we don't step back until you've secured your first 5 operating sites.",
    },
    {
      n: "03",
      title: "You take over, everything's yours",
      body: "Handover of the full system, a documented profile of your 5 sites, and an introduction to a trusted machine supplier so you can continue independently.",
    },
  ];
  return (
    <section
      data-testid="section-guarantee"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#FE7200]/[0.05] to-transparent" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <SectionEyebrow>Section III</SectionEyebrow>
        <SectionTitle>How we work with you</SectionTitle>
        <p className="mt-4 max-w-3xl text-zinc-300">
          We don't promise revenue numbers — performance depends on how you run
          the business day-to-day. What we do commit to is building the system
          for you and staying alongside you through outreach until you have
          five operating sites.
        </p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div
              key={s.n}
              className="rounded-2xl border border-white/10 bg-[#121212] p-6"
            >
              <div className="text-4xl font-display font-black text-[#FE7200] leading-none">
                {s.n}
              </div>
              <div className="mt-4 font-display text-lg font-bold text-white">
                {s.title}
              </div>
              <p className="mt-2 text-sm text-zinc-300 leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-xs text-zinc-500 max-w-3xl">
          Note: vending revenue varies by site, product mix, service frequency
          and operator effort. We make no guarantees about specific returns or
          income.
        </p>
      </div>
    </section>
  );
}

/* ------------------------- IV. What's Included ------------------------- */
function WhatsIncluded() {
  const groups = [
    {
      icon: Sparkles,
      title: "A. Complete system build",
      lines: [
        "Professional vending business website (design, copy, forms, SEO foundation)",
        "Inbound funnel — content, forms and Google Ads foundation",
        "Outbound outreach playbook — property manager email scripts, cold-call templates, LinkedIn sequences",
        "Marketing collateral — proposal decks, flyers, brochures",
      ],
    },
    {
      icon: GraduationCap,
      title: "B. Training & coaching",
      lines: [
        "Operations training — stock, servicing, customer support",
        "Sales training — pitching property owners and closing site agreements",
        "Regular coaching calls while you run outreach and negotiate sites",
      ],
    },
    {
      icon: Megaphone,
      title: "C. Site acquisition support",
      lines: [
        "Hands-on support with outreach until you've secured 5 operating sites",
        "AI-informed site research and prioritisation help",
        "Warm property-owner introductions from our network where available",
      ],
    },
    {
      icon: BadgeCheck,
      title: "D. Handover",
      lines: [
        "Full ownership of the built system — website, funnel and playbook",
        "Documented profile of each of your 5 secured sites",
        "Introduction to a trusted machine supplier for procurement",
      ],
    },
  ];
  return (
    <section
      data-testid="section-whats-included"
      className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5"
    >
      <SectionEyebrow>Section IV</SectionEyebrow>
      <SectionTitle>What's included in the $35,000</SectionTitle>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {groups.map((g) => (
          <div
            key={g.title}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6"
          >
            <g.icon className="h-6 w-6 text-[#FE7200]" />
            <div className="mt-3 font-display text-lg font-bold">{g.title}</div>
            <ul className="mt-3 space-y-2">
              {g.lines.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm text-zinc-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#FE7200]" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------- V. Timeline ---------------------------- */
function TimelineSection() {
  const phases = [
    { phase: "1 · System build", activity: "Website, funnel, outreach playbook and marketing collateral built and delivered", timeline: "Weeks 1–3" },
    { phase: "2 · Training & launch", activity: "Operations + sales training, outreach kick-off, inbound funnel live", timeline: "Weeks 3–4" },
    { phase: "3 · Site acquisition (with us)", activity: "Ongoing outreach, coaching calls, warm intros where available — hands-on until 5 sites secured", timeline: "Rolling" },
    { phase: "4 · Handover", activity: "Full system + 5-site profile handed over, introduction to trusted machine supplier", timeline: "On completion" },
    { phase: "5 · You run independently", activity: "You own the system, sites and supplier relationship — expand at your pace", timeline: "Ongoing" },
  ];
  return (
    <section
      data-testid="section-timeline"
      className="border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <SectionEyebrow>Section V</SectionEyebrow>
        <SectionTitle>How the engagement runs</SectionTitle>
        <p className="mt-4 max-w-3xl text-zinc-300">
          The system is built in the first few weeks. Site acquisition timing
          depends on your effort and market conditions — we stay involved
          through that phase and only step back once you're at five operating
          sites.
        </p>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212] text-left">
              <tr>
                {["Phase", "Activity", "Timing"].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {phases.map((p, i) => (
                <tr key={p.phase} data-testid={`timeline-row-${i}`}>
                  <td className="px-5 py-4 font-semibold text-white whitespace-nowrap">{p.phase}</td>
                  <td className="px-5 py-4 text-zinc-300">{p.activity}</td>
                  <td className="px-5 py-4 whitespace-nowrap text-[#FE7200] font-semibold">
                    {p.timeline}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* -------------------------- VI. Investment -------------------------- */
function InvestmentSection() {
  return (
    <section
      data-testid="section-investment"
      className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-28 border-t border-white/5"
    >
      <SectionEyebrow>Section VI</SectionEyebrow>
      <SectionTitle>Investment</SectionTitle>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-[#FE7200]/40 bg-gradient-to-br from-[#FE7200]/15 to-transparent p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">
            Total Package Fee
          </div>
          <div className="mt-2 font-display text-5xl font-black text-white">$35,000</div>
          <p className="mt-3 text-sm text-zinc-200">
            One-time investment. No hidden fees and no ongoing costs. Includes
            the entire system build, training, hands-on coaching until 5 sites,
            and handover.
          </p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FE7200]">
            Payment Structure
          </div>
          <ul className="mt-4 space-y-3 text-sm text-zinc-200">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#FE7200]/20 text-[#FE7200] font-bold">
                1
              </span>
              <span>
                <span className="font-semibold text-white">$17,500 (50%)</span> deposit
                to commence — kicks off the system build and training plan.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#FE7200]/20 text-[#FE7200] font-bold">
                2
              </span>
              <span>
                <span className="font-semibold text-white">$17,500 (50%)</span> on
                system delivery and outreach launch — before we begin working
                on site acquisition with you.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- VII. Benefits ---------------------------- */
function BenefitsSection() {
  const rows = [
    ["Complete system build", "Website, funnel, playbook and marketing collateral ready from day one"],
    ["Inbound + outbound strategy", "Two channels working in parallel to find and win sites"],
    ["Hands-on coaching", "Regular coaching while you approach property owners and negotiate sites"],
    ["We stay until 5 sites", "Our involvement continues until you've secured your first 5 operating sites"],
    ["Full system ownership", "Everything we build is yours to keep and evolve"],
    ["Machine supplier access", "Introduction to a trusted supplier for machine procurement at handover"],
    ["No ongoing fees", "Pay once, own the system for life"],
  ];
  return (
    <section
      data-testid="section-benefits"
      className="border-t border-white/5"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <SectionEyebrow>Section VII</SectionEyebrow>
        <SectionTitle>Benefits to you</SectionTitle>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.map(([feature, benefit]) => (
            <div
              key={feature}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-[#121212] p-5"
            >
              <div className="mt-1 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-[#FE7200]/15">
                <Users className="h-4 w-4 text-[#FE7200]" />
              </div>
              <div>
                <div className="font-display text-base font-bold text-white">{feature}</div>
                <div className="mt-0.5 text-sm text-zinc-300">{benefit}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------- Application form section ---------------------- */
function ApplicationSection() {
  return (
    <section
      id="apply"
      data-testid="section-apply"
      className="border-t border-white/5"
    >
      <div className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <SectionEyebrow>Apply now</SectionEyebrow>
        <SectionTitle>Start your vending business application</SectionTitle>
        <p className="mt-4 max-w-3xl text-zinc-300">
          Tell us a bit about you. We'll review your application and get in touch within
          one business day to walk through the system build, answer questions, and
          discuss timing for kicking off your outreach.
        </p>
        <div className="mt-10">
          <BusinessApplicationForm testIdPrefix="biz-app" />
        </div>
      </div>
    </section>
  );
}

/* ----------------------------- shared bits ----------------------------- */
function SectionEyebrow({ children }) {
  return (
    <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FE7200]">
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tight">
      {children}
    </h2>
  );
}
